import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Users, Search, Calendar, MessageCircle, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSquads } from '../hooks/useSquads';
import BottomSheet from '../../components/common/BottomSheet';

const MAX_SQUADS = 3;

/** Mobile bottom-sheet quick-nav for Squads — shows the user's most relevant squads first,
 *  with direct links into their most-used tabs, instead of dropping them on the full search list. */
const SquadsQuickPanel = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { fetchUserGroups } = useSquads(false);
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUserGroups({ limit: MAX_SQUADS });
      setSquads(Array.isArray(result?.data) ? result.data : []);
    } finally {
      setLoading(false);
    }
  }, [fetchUserGroups]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const goToSquad = (squadId, tab) => {
    onClose();
    navigate(`/squads/${squadId}${tab ? `?tab=${tab}` : ''}`);
  };

  const goToList = () => {
    onClose();
    navigate('/squads');
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Users className={`w-5 h-5 ${theme.primary}`} />
            <span className={`font-semibold ${theme.text}`}>Your Squads</span>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.cardHover}`}>
            <X className={`w-4 h-4 ${theme.textMuted}`} />
          </button>
        </div>

        <div className={`h-px ${theme.border} bg-current opacity-10 mx-4`} />

        <div className="px-4 py-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${theme.primary.replace('text-', 'border-')}`} />
            </div>
          ) : squads.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className={`text-sm ${theme.textMuted} mb-3`}>You haven't joined a squad yet</p>
            </div>
          ) : (
            <div className="space-y-3 mb-3">
              {squads.map(squad => (
                <div key={squad.id} className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-3`}>
                  <button
                    onClick={() => goToSquad(squad.id)}
                    className={`flex items-center justify-between w-full mb-2 text-left`}
                  >
                    <span className={`font-semibold text-sm ${theme.text}`}>{squad.name}</span>
                    <span className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
                      {squad.memberCount} {squad.memberCount === 1 ? 'member' : 'members'}
                    </span>
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => goToSquad(squad.id, 'members')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <Users className="w-3.5 h-3.5" /> Members
                    </button>
                    <button
                      onClick={() => goToSquad(squad.id, 'current-event')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <Calendar className="w-3.5 h-3.5" /> Event
                    </button>
                    <button
                      onClick={() => goToSquad(squad.id, 'chat')}
                      className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium ${theme.cardHover} ${theme.border} ${theme.textSecondary}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Chat
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={goToList}
            className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg ${theme.cardHover} ${theme.textSecondary} text-sm font-medium`}
          >
            <span className="flex items-center gap-2"><Search className="w-4 h-4" /> Browse all squads</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
    </BottomSheet>
  );
};

export default SquadsQuickPanel;
